import {
    ApolloClient,
    ApolloLink,
    ApolloProvider,
    FetchResult,
    InMemoryCache,
    NormalizedCacheObject,
    Observable,
    Operation,
    split,
} from '@apollo/client';
import {setContext} from '@apollo/client/link/context';
import {NextLink} from '@apollo/client/link/core/types';
import {onError} from '@apollo/client/link/error';
import {GraphQLWsLink} from '@apollo/client/link/subscriptions';
import {getMainDefinition} from '@apollo/client/utilities';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import {createClient} from 'graphql-ws';
import {useSessionRedux} from 'hooks';
import {useAlert} from 'hooks/useAlert';
import {ReactNode} from 'react';
import {useSelector} from 'react-redux';
import {sessionSelector} from 'store/auth/sessionSlice';
import {removeTypenameLink} from './removeTypeNameInAppoloResponse';

let isRefreshing = false;
let pendingRequests: (() => void)[] = [];

const resolvePendingRequests = () => {
    console.log('[Apollo] Resolving pending requests');
    pendingRequests.forEach(callback => callback());
    pendingRequests = [];
};

interface ApolloWrapperProviderProps {
    children: ReactNode;
}

export const ApolloWrapperProvider = ({children}: ApolloWrapperProviderProps) => {
    const {clearSessionInRedux, refrestTokenHandler} = useSessionRedux();
    const {jwtToken, refreshToken} = useSelector(sessionSelector);
    const {invokeAlert} = useAlert();
    const baseUrl = process.env.REACT_APP_API_BASE_URL || '';

    const httpLink = createUploadLink({
        uri: `${baseUrl}/graphql`,
        headers: {
            'Apollo-Require-Preflight': 'true',
            'keep-alive': 'true',
        },
    });

    const wsLink = new GraphQLWsLink(
        createClient({
            url: `${baseUrl}/graphql`,
            connectionParams: () => ({
                authorization: jwtToken ? `Bearer ${jwtToken}` : '',
            }),
        }),
    );

    const authLink = setContext((_, {headers}) => ({
        headers: {
            ...headers,
            authorization: jwtToken ? `Bearer ${jwtToken}` : '',
        },
    }));

    const queueRequest = (observer: any, forward: NextLink, operation: Operation) => {
        pendingRequests.push(() => {
            forward(operation).subscribe({
                next: value => observer.next?.(value),
                error: err => observer.error?.(err),
                complete: () => observer.complete?.(),
            });
        });
    };

    const processPendingRequests = (observer: any, newAccessToken: string, operation: Operation, forward: NextLink) => {
        operation.setContext(({headers = {}}: {headers?: Record<string, string>}) => ({
            headers: {
                ...headers,
                authorization: `Bearer ${newAccessToken}`,
            },
        }));

        resolvePendingRequests();

        forward(operation).subscribe({
            next: value => observer.next?.(value),
            error: err => observer.error?.(err),
            complete: () => observer.complete?.(),
        });
    };

    const handleTokenRefresh = async (observer: any, operation: Operation, forward: NextLink) => {
        const refreshUrl = `${baseUrl}/api/auth/refresh-token`;

        try {
            const res = await fetch(refreshUrl, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({token: refreshToken}),
            });

            if (!res.ok) throw new Error(`[HTTP ${res.status}] Refresh token request failed`);

            const json: {
                JWTToken: string;
                RefreshToken: string;
                errors?: any;
                message?: string;
            } = await res.json();

            if (json.errors || json.message === 'Token expired') {
                throw new Error(json.message || 'Refresh token failed.');
            }

            refrestTokenHandler({
                token: json.JWTToken,
                refreshToken: json.RefreshToken,
            });

            processPendingRequests(observer, json.JWTToken, operation, forward);
        } catch (error) {
            console.error('[Apollo] Token refresh failed:', error);
            // invokeAlert('Session expired. Please log in again.');
            clearSessionInRedux();
            observer.error?.(error);
        } finally {
            isRefreshing = false;
            console.info('[Apollo] Token refresh complete.');
        }
    };

    const errorLink = onError(({graphQLErrors, networkError, operation, forward}) => {
        if (graphQLErrors) {
            for (const err of graphQLErrors) {
                if (err.extensions?.code === 'AUTH_NOT_AUTHENTICATED') {
                    console.warn('[Apollo] AUTH_NOT_AUTHENTICATED detected');

                    if (!isRefreshing) {
                        isRefreshing = true;
                        return new Observable<FetchResult>(observer => {
                            handleTokenRefresh(observer, operation, forward);
                        });
                    } else {
                        return new Observable<FetchResult>(observer => {
                            queueRequest(observer, forward, operation);
                        });
                    }
                } else {
                    invokeAlert(err.message);
                }
            }
        }

        if (networkError) {
            console.error('[Apollo] Network error:', networkError);
        }
    });

    const httpLinkWithMiddleware = ApolloLink.from([
        removeTypenameLink,
        errorLink,
        authLink,
        httpLink as unknown as ApolloLink, // because `createUploadLink` isn't typed as `ApolloLink`
    ]);

    const splitLink = split(
        ({query}) => {
            const definition = getMainDefinition(query);
            return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
        },
        wsLink,
        httpLinkWithMiddleware,
    );

    const client: ApolloClient<NormalizedCacheObject> = new ApolloClient({
        link: splitLink,
        cache: new InMemoryCache({
            addTypename: false,
        }),
        defaultOptions: {
            watchQuery: {fetchPolicy: 'no-cache', errorPolicy: 'none'},
            query: {fetchPolicy: 'no-cache', errorPolicy: 'none'},
            mutate: {errorPolicy: 'none'},
        },
    });

    console.log('[Apollo] ApolloProvider initialized');

    return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
